import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { loading_02 } from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";

import { LoadGenerateDocumentsModal } from "../components/load-generate-documents-modal/load-generate-documents-modal.tsx";
import {
	useGenerateLoadDocuments,
	usePendingLoadDocumentGenerations,
} from "./use-generate-load-documents.ts";

type UseLoadDocumentGenerationParams = {
	load: Load | null;
	isInternal: boolean;
};

/**
 * External customers can (re)generate the LTL BOL and labels for a load from
 * the documents flyout. Clicking the header action calls generate-documents;
 * once that succeeds the tender modal opens with the same View/Download/Send
 * behavior as quoting.
 */
export const useLoadDocumentGeneration = ({
	load,
	isInternal,
}: UseLoadDocumentGenerationParams): {
	headerAction: ReactNode;
	modal: ReactNode;
} => {
	// The modal is bound to the load the generation request was made for, not
	// whichever load the flyout currently shows.
	const [modalLoad, setModalLoad] = useState<Load | null>(null);
	const { mutateAsync: generateLoadDocuments } = useGenerateLoadDocuments();
	const pendingPoNumbers = usePendingLoadDocumentGenerations();
	// Synchronous guard against double submission before the mutation cache
	// re-renders with the new pending request.
	const submittedPoNumbersRef = useRef(new Set<number>());
	// LD key "TRAX-LTLEnableDocRegenExternal" (camelCased by the LD React SDK).
	const { isEnabled: isDocRegenEnabled } = useFeatureFlagGate(
		"traxLtlEnableDocRegenExternal",
	);

	const poNumber = load?.poNumber ?? undefined;
	const canGenerate =
		isDocRegenEnabled && !isInternal && load?.rateType === "LTL" && !!poNumber;

	// Reset load-specific modal state whenever the flyout switches loads.
	const [previousPoNumber, setPreviousPoNumber] = useState(poNumber);
	if (previousPoNumber !== poNumber) {
		setPreviousPoNumber(poNumber);
		setModalLoad(null);
	}

	// Read inside async completion callbacks to detect a load switch mid-request.
	const activePoNumberRef = useRef(poNumber);
	useEffect(() => {
		activePoNumberRef.current = poNumber;
	}, [poNumber]);

	const isGeneratingThisLoad =
		poNumber !== undefined && pendingPoNumbers.includes(poNumber);

	const handleGenerateClick = useCallback(() => {
		const requestedPoNumber = load?.poNumber;
		if (!load || !requestedPoNumber) return;
		if (
			submittedPoNumbersRef.current.has(requestedPoNumber) ||
			pendingPoNumbers.includes(requestedPoNumber)
		) {
			return;
		}
		const requestedLoad = load;
		submittedPoNumbersRef.current.add(requestedPoNumber);
		generateLoadDocuments(requestedPoNumber)
			.then(() => {
				// Only open for the load the user is still looking at.
				if (activePoNumberRef.current === requestedPoNumber) {
					setModalLoad(requestedLoad);
				}
			})
			.catch(() => {
				// The mutation's onError already surfaced the failure to the user.
			})
			.finally(() => {
				submittedPoNumbersRef.current.delete(requestedPoNumber);
			});
	}, [load, pendingPoNumbers, generateLoadDocuments]);

	const handleCloseModal = useCallback(() => {
		setModalLoad(null);
	}, []);

	if (!canGenerate || !load) {
		return { headerAction: undefined, modal: null };
	}

	return {
		headerAction: (
			<Button
				disabled={isGeneratingThisLoad}
				id={"borderless-button"}
				onClick={handleGenerateClick}
				startIcon={<SvgIcon icon={loading_02} />}
			>
				{isGeneratingThisLoad ? "Generating..." : "Generate LTL BOL and Labels"}
			</Button>
		),
		modal:
			modalLoad?.poNumber === poNumber ? (
				<LoadGenerateDocumentsModal
					load={modalLoad}
					onToggleModal={handleCloseModal}
					showModal={true}
				/>
			) : null,
	};
};
